/**
 * Submission-URL resolver for /api/send.
 *
 * # Why this module exists (Phase M+5 — Option B)
 *
 * The M+3.5 architecture moved the customer-submission pipeline (Cosmos
 * write + Microsoft Graph email + audit log) into the admin platform.
 * The public SWA's `apps/public/api/send/` Function was left behind as
 * dead code — the SWA build step quietly drops it and any POST to the
 * public origin's `/api/send` 405s.
 *
 * The deployer's M+5 exit report (Open finding "public SWA `/api/send`
 * Function not deployed") explicitly recommends **Option B**: have the
 * public bundle POST cross-origin to the admin's `/api/send` instead.
 * The admin's CORS allowlist already permits `ddg-promotions.com` + the
 * public custom domain; the iframe-embedded bundle uses the public
 * custom-domain origin, which the admin trusts.
 *
 * # The contract
 *
 *   - `VITE_ADMIN_BASE_URL` unset / empty / whitespace → same-origin
 *     relative path (`/api/send`). This is the dev default; `npm run
 *     dev --workspace=apps/public` proxies `/api/send` to the local
 *     admin at port 3001 via `vite.config.ts`.
 *
 *   - `VITE_ADMIN_BASE_URL` set → absolute URL `${base}/api/send` after
 *     stripping any trailing slash on `base`.
 *
 * Trailing-slash handling matters: an editor running `vercel env pull`
 * or an operator typing the staging URL by hand will sometimes leave a
 * `/`. We strip exactly one to keep the result deterministic.
 *
 * # Test seam
 *
 * Like `preview.ts`, this module exposes a test override
 * (`setAdminBaseUrlForTest`) instead of mocking `import.meta.env` at the
 * module-graph level. happy-dom can't redefine `import.meta`, and Vitest's
 * stub helpers don't reach Vite-injected env values reliably.
 */

let __testAdminBaseUrlOverride: string | undefined;
let __testHasOverride = false;

/**
 * Test-only seam — override `VITE_ADMIN_BASE_URL` for the duration of a
 * test case. Pass `undefined` to clear the override (return to reading
 * `import.meta.env`).
 */
export function setAdminBaseUrlForTest(value: string | undefined): void {
  __testAdminBaseUrlOverride = value;
  __testHasOverride = true;
}

/**
 * Reset the test override so the next call reads from
 * `import.meta.env.VITE_ADMIN_BASE_URL` again. Tests should call this in
 * an `afterEach`; production code never calls it.
 */
export function clearAdminBaseUrlOverrideForTest(): void {
  __testAdminBaseUrlOverride = undefined;
  __testHasOverride = false;
}

function readConfiguredAdminBaseUrl(): string | undefined {
  if (__testHasOverride) return __testAdminBaseUrlOverride;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta = (import.meta as any).env as Record<string, string> | undefined;
    return meta?.['VITE_ADMIN_BASE_URL'];
  } catch {
    return undefined;
  }
}

/**
 * Resolve an admin API path against `VITE_ADMIN_BASE_URL`. The ONE place the
 * env-read + trailing-slash strip live, so every public→admin endpoint
 * (`/api/send`, `/api/send/logo`, …) targets the SAME base and a mis-route
 * regresses in one spot.
 *
 *   - With `VITE_ADMIN_BASE_URL=https://admin.example.com` + path `/api/send`
 *     → `https://admin.example.com/api/send`
 *   - With it unset / empty / whitespace → the same-origin relative `path`
 *     (dev proxy forwards to localhost:3001).
 *
 * `path` must start with `/`.
 */
export function resolveAdminApiUrl(path: string): string {
  const raw = readConfiguredAdminBaseUrl();
  if (!raw || raw.trim().length === 0) {
    return path;
  }
  const trimmed = raw.trim().replace(/\/+$/, '');
  return `${trimmed}${path}`;
}

/**
 * Resolve the URL the public bundle should POST `/api/send` payloads to.
 *
 *   - With `VITE_ADMIN_BASE_URL=https://admin.example.com` →
 *     `https://admin.example.com/api/send`
 *   - With it unset / empty / whitespace →
 *     `/api/send` (same-origin; dev proxy forwards to localhost:3001)
 */
export function resolveSendUrl(): string {
  return resolveAdminApiUrl('/api/send');
}

/**
 * Contact-form payload (redesign pass A). The landing page's contact form
 * previously opened a `mailto:` and showed a browser alert; it now POSTs
 * through the same `/api/send` pipeline the quote submission uses, tagged
 * `kind: "contact"` so the admin handler can branch on it.
 *
 * NOTE for the backend pass: the admin's `/api/send` currently validates
 * `NewSubmissionInputSchema` only — it needs a `kind: "contact"` branch
 * before this delivers in production. In the sandbox the endpoint is
 * unreachable and the form's inline error state is the expected outcome.
 */
export interface ContactMessage {
  name: string;
  email: string;
  message: string;
}

/**
 * POST a contact message to the resolved `/api/send` endpoint.
 *
 * Throws on network failure or any non-2xx response; callers render the
 * inline error state. Resolves (void) on success.
 */
export async function sendContactMessage(input: ContactMessage): Promise<void> {
  const response = await fetch(resolveSendUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'contact',
      name: input.name,
      email: input.email,
      message: input.message,
      submittedAt: new Date().toISOString(),
    }),
  });
  if (!response.ok) {
    throw new Error(`Contact message failed: HTTP ${response.status}`);
  }
}
